import { AlertCircle, Loader2, Plus, Trash2, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";

import type { AssetGroup, PendingUpload } from "./types";

import { uploadAsset } from "../../lib/assets-api";

type AssetUploadBlockProps = {
  isDisabled?: boolean;
  onChange: (next: AssetGroup) => void;
  requestId: null | number;
  value: AssetGroup;
};

export function AssetUploadBlock({
  isDisabled = false,
  onChange,
  requestId,
  value,
}: AssetUploadBlockProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkInput, setLinkInput] = useState("");
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const uploadFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      const id = crypto.randomUUID();

      setPending((prev) => [
        ...prev,
        { id, name: file.name, status: "uploading" },
      ]);

      if (requestId === null) {
        setPending((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, status: "error" } : item,
          ),
        );
        continue;
      }

      uploadAsset(file, requestId)
        .then((asset) => {
          onChange({
            ...value,
            files: [...value.files, asset],
          });
          setPending((prev) => prev.filter((item) => item.id !== id));
        })
        .catch(() => {
          setPending((prev) =>
            prev.map((item) =>
              item.id === id ? { ...item, status: "error" } : item,
            ),
          );
        });
    }
  };

  // No delete route exists yet (ADR step 7) — the control performs no
  // action and is rendered disabled rather than clickable-with-no-effect.
  const addLink = () => {
    const trimmed = linkInput.trim();

    if (!trimmed) return;
    onChange({ ...value, links: [...value.links, { url: trimmed }] });
    setLinkInput("");
  };

  const removeLink = (index: number) =>
    onChange({
      ...value,
      links: value.links.filter((_, current) => current !== index),
    });

  const hasFiles = value.files.length > 0 || pending.length > 0;

  return (
    <div className="flex flex-col gap-md">
      <span className="text-sm font-medium text-text-secondary">Add files</span>

      <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
        <button
          className="flex flex-col items-center justify-center gap-sm rounded-xl border border-dashed border-border-primary bg-bg-secondary px-lg py-xl text-center outline-hidden transition-colors hover:bg-bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isDisabled}
          onClick={() => inputRef.current?.click()}
          type="button"
        >
          <span className="flex size-10 items-center justify-center rounded-lg border border-border-secondary bg-bg-primary text-text-secondary">
            <UploadCloud className="size-5" />
          </span>
          <span className="text-sm text-text-secondary">
            <span className="font-semibold text-fg-brand-primary">
              Click to upload
            </span>{" "}
            or drag and drop
          </span>
          <span className="text-xs text-text-tertiary">
            PNG, JPG, SVG, PDF…
          </span>
        </button>

        <div className="flex max-h-[200px] min-h-[80px] flex-col items-center justify-start overflow-y-auto rounded-xl border border-dashed border-border-primary p-lg">
          {hasFiles ? (
            <ul className="flex w-full flex-col gap-xs">
              {value.files.map((file) => (
                <li
                  className="flex items-center justify-between gap-sm rounded-lg bg-bg-secondary px-md py-sm text-sm text-text-primary"
                  key={file.attachmentId}
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    aria-label={`Remove ${file.name}`}
                    className="shrink-0 cursor-not-allowed text-text-tertiary opacity-60"
                    disabled
                    type="button"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              ))}
              {pending.map((item) => (
                <li
                  className="flex items-center justify-between gap-sm rounded-lg bg-bg-secondary px-md py-sm text-sm"
                  key={item.id}
                >
                  <span className="truncate text-text-tertiary">
                    {item.name}
                  </span>
                  {item.status === "error" ? (
                    <AlertCircle className="size-4 shrink-0 text-text-error-primary" />
                  ) : (
                    <Loader2 className="size-4 shrink-0 animate-spin text-text-tertiary" />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-sm text-text-tertiary">
              No files yet. Use the upload area to add assets for every
              variations.
            </p>
          )}
        </div>
      </div>

      <input
        className="hidden"
        multiple
        onChange={(event) => {
          uploadFiles(event.target.files);
          event.target.value = "";
        }}
        ref={inputRef}
        type="file"
      />

      <span className="text-sm font-medium text-text-secondary">Add links</span>

      <div className="flex items-center gap-sm">
        <input
          className="h-11 flex-1 rounded-lg border border-border-primary bg-bg-primary px-md text-sm text-text-primary outline-none placeholder:text-text-placeholder"
          disabled={isDisabled}
          onChange={(event) => setLinkInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addLink();
            }
          }}
          placeholder="Add link here"
          value={linkInput}
        />
        <button
          aria-label="Add link"
          className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border-primary text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isDisabled}
          onClick={addLink}
          type="button"
        >
          <Plus className="size-5" />
        </button>
      </div>

      {value.links.length > 0 ? (
        <ul className="flex flex-col gap-xs">
          {value.links.map((link, index) => (
            <li
              className="flex items-center justify-between gap-sm rounded-lg bg-bg-secondary px-md py-sm text-sm text-text-primary"
              key={`${link.url}-${index}`}
            >
              <span className="truncate">{link.url}</span>
              <button
                aria-label={`Remove ${link.url}`}
                className="shrink-0 text-text-tertiary transition-colors hover:text-text-error-primary"
                onClick={() => removeLink(index)}
                type="button"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-xs text-text-tertiary">
        When sharing links please grant access to{" "}
        <span className="text-fg-brand-primary">
          admin@nolimitcreatives.com
        </span>{" "}
        for Google Drive, and{" "}
        <span className="text-fg-brand-primary">teams@nlc.com</span> for
        Dropbox.
      </p>
    </div>
  );
}
